import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { PrimaryButton, SecondaryButton } from "@/components/flow/Primitives";
import { applyConsent, getConsent, isOwnerSurface, setConsent } from "@/lib/consent";
import { EASE } from "@/lib/motion";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
   Cookie notice — a quiet card, bottom-left on desktop; on phones a compact
   sheet flush with the bottom edge (above the portal's tab bar when there is
   one). While it is open on a phone the page reserves the sheet's height
   (html.consent-open + --consent-h), so no button can end up permanently
   hidden behind it. Two equal choices, one link to the policy. Re-opened as
   "Cookie settings" (footer) with a switch for the analytics category.
   Never shown on tenant-branded owner surfaces.
--------------------------------------------------------------------------- */

const MOBILE_NAV = '[data-testid="mobile-bottom-nav"]';

export function ConsentBanner() {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [navH, setNavH] = useState(0);
  const ref = useRef(null);
  const hidden = isOwnerSurface(pathname);

  // Sit above the portal's mobile tab bar when it is on screen; flush with the bottom everywhere else.
  useEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const nav = document.querySelector(MOBILE_NAV);
      const h = nav && window.getComputedStyle(nav).display !== "none" ? Math.round(nav.getBoundingClientRect().height) : 0;
      setNavH(h);
    };
    const id = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => { window.cancelAnimationFrame(id); window.removeEventListener("resize", measure); };
  }, [open, pathname]);

  // Reserve the sheet's height under the page content on phones so nothing is hidden behind it.
  useEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!open || !el) {
      root.classList.remove("consent-open");
      root.style.removeProperty("--consent-h");
      return undefined;
    }
    const apply = () => {
      root.classList.add("consent-open");
      root.style.setProperty("--consent-h", `${Math.round(el.getBoundingClientRect().height) + navH}px`);
    };
    apply();
    // Measure on the next frame (never inside the observer callback) and cancel that frame on cleanup — otherwise a
    // frame scheduled during the exit animation would re-add the padding after the banner is gone.
    let raf = 0;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        apply();
      });
    }) : null;
    if (ro) ro.observe(el);
    return () => {
      if (ro) ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
      root.classList.remove("consent-open");
      root.style.removeProperty("--consent-h");
    };
  }, [open, settings, navH]);

  // First visit: ask. Later visits: just apply the stored choice.
  useEffect(() => {
    const c = getConsent();
    applyConsent(c, pathname);
    if (!c && !hidden) setOpen(true);
  }, [pathname, hidden]);

  // Footer "Cookie settings" → reopen with the switch.
  useEffect(() => {
    const onOpen = () => {
      setAnalytics(!!getConsent()?.analytics);
      setSettings(true);
      setOpen(true);
    };
    window.addEventListener("listed:consent:open", onOpen);
    return () => window.removeEventListener("listed:consent:open", onOpen);
  }, []);

  const decide = (value) => {
    setConsent({ analytics: value });
    setOpen(false);
    setSettings(false);
  };

  if (hidden) return null;
  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          key="consent"
          role="dialog"
          aria-modal="false"
          aria-labelledby="consent-title"
          aria-describedby="consent-text"
          initial={{ opacity: 0, y: reduced ? 0 : 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduced ? 0 : 12 }}
          transition={{ duration: 0.24, ease: EASE }}
          ref={ref}
          style={{ "--consent-nav": `${navH}px` }}
          className={cn("fixed z-[59] left-0 right-0 bottom-[var(--consent-nav)] rounded-t-[18px] border-t border-x border-hairline sm:left-6 sm:right-auto sm:bottom-6 sm:w-[400px] sm:rounded-[18px] sm:border bg-surface/95 backdrop-blur-md text-ink shadow-[0_24px_60px_-20px_rgba(21,19,15,0.35),0_0_0_1px_rgba(21,19,15,0.03)]")}
          data-testid="consent-banner"
          data-mode={settings ? "settings" : "ask"}
        >
          <div className="px-4 pt-3.5 pb-[max(14px,env(safe-area-inset-bottom))] sm:p-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-fill text-ink"><Cookie className="h-4 w-4" strokeWidth={1.8} /></span>
              <div className="min-w-0 flex-1">
                <p id="consent-title" className="text-[13.5px] sm:text-[14px] font-semibold tracking-[-0.01em] leading-[1.3]">{settings ? t("consent.settings_title") : t("consent.title")}</p>
                <p id="consent-text" className="mt-0.5 sm:mt-1 text-[12.5px] sm:text-[13px] text-ink/65 leading-[1.45] sm:leading-[1.5]">
                  {t("consent.text")} <Link to="/privacy#cookies" className="text-ink underline underline-offset-2 decoration-ink/30 hover:decoration-ink" data-testid="consent-privacy-link">{t("legal.privacy")}</Link>
                </p>
              </div>
              {settings && (
                <button type="button" onClick={() => { setOpen(false); setSettings(false); }} aria-label={t("common.close")} className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ink/50 hover:text-ink hover:bg-fill transition-colors focus-visible:outline-none focus-visible:shadow-ring" data-testid="consent-close"><X className="h-4 w-4" /></button>
              )}
            </div>

            {settings ? (
              <div className="mt-4 divide-y divide-hairline rounded-[12px] border border-hairline">
                <div className="flex items-center justify-between gap-4 px-3.5 py-3">
                  <div className="min-w-0"><p className="text-[13px] font-medium">{t("consent.cat.necessary")}</p><p className="text-[12px] text-ink/55 leading-[1.4]">{t("consent.cat.necessary_sub")}</p></div>
                  <span className="text-[11.5px] font-medium text-ink/50 shrink-0" data-testid="consent-necessary-always">{t("consent.always_on")}</span>
                </div>
                <div className="flex items-center justify-between gap-4 px-3.5 py-3">
                  <div className="min-w-0"><p className="text-[13px] font-medium">{t("consent.cat.analytics")}</p><p className="text-[12px] text-ink/55 leading-[1.4]">{t("consent.cat.analytics_sub")}</p></div>
                  <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label={t("consent.cat.analytics")} data-testid="consent-analytics-switch" />
                </div>
              </div>
            ) : null}

            <div className="mt-3 sm:mt-4 flex flex-row gap-2">
              {settings ? (
                <PrimaryButton size="sm" variant="ink" pip={false} className="sm:ml-auto" onClick={() => decide(analytics)} data-testid="consent-save">{t("consent.save")}</PrimaryButton>
              ) : (
                <>
                  <SecondaryButton size="sm" className="flex-1" onClick={() => decide(false)} data-testid="consent-reject">{t("consent.reject")}</SecondaryButton>
                  <PrimaryButton size="sm" variant="ink" pip={false} className="flex-1" onClick={() => decide(true)} data-testid="consent-accept">{t("consent.accept")}</PrimaryButton>
                </>
              )}
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
